import { mergeAttributes, Node } from '@tiptap/core';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Plugin, PluginKey } from 'prosemirror-state';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Slice, Fragment } from 'prosemirror-model';
// eslint-disable-next-line import/no-extraneous-dependencies
import { EditorView } from 'prosemirror-view';

export interface FixedBlockOptions {}

export const FixedBlock = Node.create<FixedBlockOptions>({
  name: 'fixedBlock',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: false,

  addAttributes() {
    return {
      'data-type': {
        default: 'fixed-block',
      },
      text: {
        default: '',
        parseHTML: (element) => element.textContent || '',
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="fixed-block"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, { 'data-type': 'fixed-block', class: 'tiptap-fixed-block' }),
      HTMLAttributes.text,
    ];
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('fixedBlockProtection'),

        filterTransaction: (transaction, state) => {
          const { doc } = state;
          return !transaction.steps.some((step) => {
            if (
              'from' in step
              && 'to' in step
              && typeof step.from === 'number'
              && typeof step.to === 'number'
            ) {
              let containsFixedBlock = false;
              doc.nodesBetween(step.from, step.to, (node) => {
                if (node.type.name === 'fixedBlock') {
                  containsFixedBlock = true;
                  return false;
                }
                return true;
              });
              return containsFixedBlock;
            }
            return false;
          });
        },

        props: {
          handleDOMEvents: {
            paste(view: EditorView, event: Event) {
              if (!(event instanceof ClipboardEvent)) return false;

              event.preventDefault();
              const { clipboardData } = event;
              if (!clipboardData) return false;

              const text = clipboardData.getData('text/plain');
              if (!text) return false;

              const { state, dispatch } = view;
              const { selection } = state;

              // Prevent pasting if it would insert inside a fixedBlock
              let inFixedBlock = false;
              state.doc.nodesBetween(selection.from, selection.to, (node) => {
                if (node.type.name === 'fixedBlock') {
                  inFixedBlock = true;
                  return false;
                }
                return true;
              });
              if (inFixedBlock) return true; // Ignore paste inside fixedBlock

              const textNode = state.schema.text(text);
              const slice = new Slice(Fragment.from(textNode), 0, 0);

              dispatch(state.tr.replaceSelection(slice).scrollIntoView());
              return true;
            },

            cut(view: EditorView, event: Event) {
              if (!(event instanceof ClipboardEvent)) return false;

              const { state } = view;
              const { selection, doc } = state;
              let fixedBlockSelected = false;

              doc.nodesBetween(selection.from, selection.to, (node) => {
                if (node.type.name === 'fixedBlock') {
                  fixedBlockSelected = true;
                  return false;
                }
                return true;
              });

              if (fixedBlockSelected) {
                event.preventDefault();
                return true; // Prevent cutting fixed block
              }

              return false;
            },
          },
        },
      }),
    ];
  },
});
